<template>
  <div class="product-management-container">
    <div class="page-header">
      <h2 class="page-title">商品管理</h2>
      <button @click="openModal()" class="add-btn">新增商品</button>
    </div>

    <div class="table-card">
      <table>
        <thead>
          <tr>
            <th>商品</th>
            <th>分类</th>
            <th>价格</th>
            <th>库存</th>
            <th>状态 (上架/下架)</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in products" :key="product.id">

            <td class="product-info">
              <img :src="product.imageUrl || 'https://placehold.co/40x40'" alt="product image">
              <span>{{ product.name }}</span>
            </td>
            <td>{{ product.category }}</td>
            <td>¥{{ product.price }}</td>
            <td>{{ product.stock }}</td>
            <td>
              <label class="toggle-switch">
                <input type="checkbox" :checked="product.isOut === 1" @change="toggleStatus(product)">
                <span class="slider"></span>
              </label>
            </td>
            <td class="actions-cell">
              <button @click="openModal(product)" class="action-btn edit">编辑</button>
              <button @click="confirmDelete(product.id)" class="action-btn delete">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <h3>{{ isEditing ? '编辑商品' : '新增商品' }}</h3>
        <form @submit.prevent="saveProduct">
          <div class="form-container">
            <!-- Form Main Content -->
            <div class="form-main-content">
              <div class="form-row">
                <div class="form-group">
                  <label>商品名称</label>
                  <input v-model="currentProduct.name" type="text" required>
                </div>
                <div class="form-group">
                  <label>分类</label>
                  <input v-model="currentProduct.category" type="text">
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>价格</label>
                  <input v-model.number="currentProduct.price" type="number" step="0.01" required>
                </div>
                <div class="form-group">
                  <label>库存</label>
                  <input v-model.number="currentProduct.stock" type="number" required>
                </div>
              </div>
              <div class="form-group">
                <label>描述</label>
                <textarea v-model="currentProduct.description" rows="4"></textarea>
              </div>
              <div class="form-group-checkbox">
                <input type="checkbox" v-model="currentProduct.hot" id="isHot">
                <label for="isHot">热卖商品</label>
              </div>
            </div>

            <!-- Form Sidebar for Image Upload -->
            <div class="form-sidebar">
              <div class="form-group">
                <label>商品图片</label>
                <div class="image-uploader">
                  <img :src="imagePreview || 'https://placehold.co/300x200'" alt="product image preview" class="image-preview">
                  <input type="file" @change="handleFileChange" accept="image/*" ref="fileInput" style="display: none;">
                  <button type="button" @click="$refs.fileInput.click()" class="btn-upload">选择图片</button>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" @click="closeModal" class="btn-cancel">取消</button>
            <button type="submit" class="btn-confirm">保存</button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue';

const { appContext } = getCurrentInstance();
const $axios = appContext.config.globalProperties.$axios;
const $notify = appContext.config.globalProperties.$notify;
const $confirm = appContext.config.globalProperties.$confirm;

const products = ref([]);
const showModal = ref(false);
const isEditing = ref(false);
const selectedFile = ref(null);
const imagePreview = ref('');

const getInitialProduct = () => ({
  id: null,
  name: '',
  price: 0,
  stock: 0,
  description: '',
  category: '',
  imageUrl: '',
  hot: false,
  isOut: 1,
});

const currentProduct = ref(getInitialProduct());

const fetchProducts = async () => {
  try {
    const res = await $axios.get('/admin/products');
    products.value = res.data.data;
  } catch (error) {
    $notify.add('获取商品列表失败', 'error');
  }
};

const openModal = (product = null) => {
  if (product) {
    isEditing.value = true;
    currentProduct.value = { ...product };
    imagePreview.value = product.imageUrl; // Set image preview for existing product
  } else {
    isEditing.value = false;
    currentProduct.value = getInitialProduct();
    imagePreview.value = ''; // Reset preview
  }
  selectedFile.value = null; // Reset file input
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
    imagePreview.value = URL.createObjectURL(file);
  }
};

const saveProduct = async () => {
  try {
    // 1. If a new file is selected, upload it first
    if (selectedFile.value) {
      const formData = new FormData();
      formData.append('file', selectedFile.value);
      const res = await $axios.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      currentProduct.value.imageUrl = res.data.data; // Update imageUrl with the returned URL
    }

    // 2. Save product data (create or update)
    if (isEditing.value) {
      await $axios.put(`/admin/products/${currentProduct.value.id}`, currentProduct.value);
      $notify.add('商品更新成功!', 'success');
    } else {
      if (!currentProduct.value.imageUrl) {
        $notify.add('请先上传商品图片', 'warning');
        return;
      }
      await $axios.post('/admin/products', currentProduct.value);
      $notify.add('商品创建成功!', 'success');
    }
    await fetchProducts();
    closeModal();
  } catch (error) {
    console.error('Save product error:', error);
    $notify.add('保存商品失败', 'error');
  }
};


const confirmDelete = (id) => {
  $confirm.show({
    message: '确定要删除这个商品吗？此操作无法撤销。',
    onConfirm: async () => {
      try {
        await $axios.delete(`/admin/products/${id}`);
        $notify.add('商品删除成功', 'success');
        await fetchProducts();
      } catch (error) {
        $notify.add('删除商品失败', 'error');
      }
    }
  });
};

const toggleStatus = async (product) => {
  const newStatus = product.isOut === 1 ? 2 : 1;
  try {
    await $axios.patch(`/admin/products/${product.id}/status`, { isOut: newStatus });
    product.isOut = newStatus;
    $notify.add('商品状态更新成功！', 'success');
  } catch (error) {
    $notify.add('状态更新失败', 'error');
  }
};


onMounted(fetchProducts);
</script>

<style scoped>
/* Container and Header */
.product-management-container {
  padding: 20px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}
.page-title {
  font-size: 1.8em;
  font-weight: 600;
}
.add-btn {
  background-color: #5e72e4;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
}

/* Table Styles */
.table-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.07);
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}
th, td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
thead {
  background-color: #f9fafb;
}
.product-info {
  display: flex;
  align-items: center;
  gap: 15px;
}
.product-info img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
}
.actions-cell {
  display: flex;
  gap: 10px;
}
.action-btn {
  border: 1px solid #dcdfe6;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  background-color: #fff;
}
.action-btn.edit:hover { background-color: #ecf5ff; color: #409eff; }
.action-btn.delete:hover { background-color: #fef0f0; color: #f56c6c; }

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 28px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: #52c41a;
}
input:checked + .slider:before {
  transform: translateX(22px);
}

/* Modal and Form Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}
.modal-content {
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  width: 100%;
  max-width: 800px; /* Increased width for two-column layout */
}
.modal-content h3 {
  margin-top: 0;
  margin-bottom: 25px;
  text-align: center;
  font-size: 1.5em;
}

.form-container {
  display: flex;
  gap: 30px;
}
.form-main-content {
  flex: 2; /* Takes 2/3 of the space */
}
.form-sidebar {
  flex: 1; /* Takes 1/3 of the space */
}

.form-row {
  display: flex;
  gap: 20px;
}
.form-group {
  margin-bottom: 20px;
  flex: 1;
}
.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}
.form-group input,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
}
.form-group-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

/* Image Uploader */
.image-uploader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}
.image-preview {
  width: 100%;
  height: auto;
  max-height: 200px;
  border-radius: 8px;
  border: 1px dashed #dcdfe6;
  object-fit: cover;
}
.btn-upload {
  width: 100%;
  padding: 10px;
  border: 1px solid #5e72e4;
  color: #5e72e4;
  background-color: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}

.modal-actions {
  text-align: right;
  margin-top: 30px;
  border-top: 1px solid #f0f0f0;
  padding-top: 20px;
}
.btn-cancel, .btn-confirm {
  padding: 10px 20px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
  font-weight: 500;
}
.btn-cancel { background: #fff; }
.btn-confirm {
  background: #409eff;
  color: #fff;
  border-color: #409eff;
  margin-left: 10px;
}
</style>